import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useAuth } from '../../contexts/AuthContext';
import { biService } from '../../services/api';

/**
 * Cubo interativo (arraste e solte) — atende o item de licitacao 6.22.1.6 (drag & drop)
 * e 6.22.1.24 (cubo de decisao). Um seletor de Fonte (Atendimentos / Vacinacao /
 * Dispensacao) define a paleta de dimensoes; os chips sao arrastaveis para as zonas
 * Linhas / Colunas. Os dados vem do banco analitico do BI (bi_db_*) consultado direto
 * pelo backend (POST /bi/cubo), com RLS por municipio. A tabela cruzada e montada no cliente.
 */

type Dim = { key: string; label: string };
type ResolvedDim = { key: string; label: string; column: string };
type CuboResp = { rowDims: ResolvedDim[]; colDims: ResolvedDim[]; metric: Dim; data: any[] };

const ROWS = 'rows';
const COLS = 'cols';
const PALETTE = 'palette';

const nf = new Intl.NumberFormat('pt-BR');

const CuboInterativo: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [fontes, setFontes] = useState<Dim[]>([]);
  const [fonte, setFonte] = useState<string>('');
  const [dims, setDims] = useState<Dim[]>([]);
  const [metricas, setMetricas] = useState<Dim[]>([]);
  const [metric, setMetric] = useState<string>('');
  const [rowKeys, setRowKeys] = useState<string[]>([]);
  const [colKeys, setColKeys] = useState<string[]>([]);
  const [resp, setResp] = useState<CuboResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Carrega fontes + dimensoes/metricas da fonte (default = 1a). Ao trocar de fonte as
  // dimensoes mudam, entao resetamos as zonas Linhas/Colunas, a metrica e a tabela.
  const loadFonte = useCallback((f?: string) => {
    biService
      .getDimensoes(f)
      .then((m) => {
        setFontes(m.fontes);
        setFonte(m.fonte);
        setDims(m.dimensoes);
        setMetricas(m.metricas);
        setMetric(m.metricas[0]?.key || '');
        setRowKeys([]);
        setColKeys([]);
        setResp(null);
      })
      .catch(() => setError('Falha ao carregar as dimensoes do cubo.'));
  }, []);

  useEffect(() => {
    loadFonte();
  }, [loadFonte]);

  const paletteKeys = useMemo(
    () => dims.map((d) => d.key).filter((k) => !rowKeys.includes(k) && !colKeys.includes(k)),
    [dims, rowKeys, colKeys],
  );
  const labelOf = useCallback((k: string) => dims.find((d) => d.key === k)?.label || k, [dims]);

  const move = (key: string, target: string) => {
    setRowKeys((prev) => {
      const r = prev.filter((k) => k !== key);
      return target === ROWS ? [...r, key] : r;
    });
    setColKeys((prev) => {
      const c = prev.filter((k) => k !== key);
      return target === COLS ? [...c, key] : c;
    });
  };

  const onDragStart = (e: React.DragEvent, key: string) => e.dataTransfer.setData('text/plain', key);
  const onDrop = (e: React.DragEvent, target: string) => {
    e.preventDefault();
    const key = e.dataTransfer.getData('text/plain');
    if (key) move(key, target);
  };
  const allowDrop = (e: React.DragEvent) => e.preventDefault();

  useEffect(() => {
    if (!municipioAtivo?.id) return;
    if (rowKeys.length === 0 && colKeys.length === 0) {
      setResp(null);
      return;
    }
    setLoading(true);
    setError(null);
    biService
      .getCubo({ municipio_id: municipioAtivo.id, fonte, rows: rowKeys, columns: colKeys, metric })
      .then(setResp)
      .catch((err) => setError(err?.response?.data?.message || 'Falha ao consultar o cubo.'))
      .finally(() => setLoading(false));
  }, [municipioAtivo?.id, fonte, rowKeys, colKeys, metric]);

  // Pivota as linhas planas (group by todas as dimensoes) numa tabela cruzada.
  const pivot = useMemo(() => {
    if (!resp) return null;
    const rowCols = resp.rowDims.map((d) => d.column);
    const colCols = resp.colDims.map((d) => d.column);
    const valKey = resp.metric.label;
    const fmt = (v: any) => (v === null || v === undefined ? '' : String(v));
    const tuple = (row: any, cols: string[]) => cols.map((c) => fmt(row[c])).join(' / ');

    const rowTuples: string[] = [];
    const colTuples: string[] = [];
    const rowVals: Record<string, string[]> = {};
    const cell: Record<string, number> = {};
    const rowTotal: Record<string, number> = {};
    const colTotal: Record<string, number> = {};
    let grand = 0;

    for (const r of resp.data) {
      const rt = tuple(r, rowCols);
      const ct = tuple(r, colCols);
      if (!(rt in rowVals)) {
        rowVals[rt] = rowCols.map((c) => fmt(r[c]));
        rowTuples.push(rt);
      }
      if (!colTuples.includes(ct)) colTuples.push(ct);
      const v = Number(r[valKey]) || 0;
      cell[`${rt}||${ct}`] = (cell[`${rt}||${ct}`] || 0) + v;
      rowTotal[rt] = (rowTotal[rt] || 0) + v;
      colTotal[ct] = (colTotal[ct] || 0) + v;
      grand += v;
    }
    return {
      rowDims: resp.rowDims,
      colDims: resp.colDims,
      hasCols: colCols.length > 0,
      rowTuples,
      colTuples,
      rowVals,
      cell,
      rowTotal,
      colTotal,
      grand,
      valLabel: valKey,
    };
  }, [resp]);

  const renderChip = (key: string, from: string) => (
    <Chip
      key={key}
      label={labelOf(key)}
      icon={<DragIndicatorIcon />}
      draggable
      onDragStart={(e) => onDragStart(e, key)}
      onDelete={from !== PALETTE ? () => move(key, PALETTE) : undefined}
      sx={{ cursor: 'grab', mb: 0.5 }}
      color={from === PALETTE ? 'default' : 'primary'}
      variant={from === PALETTE ? 'outlined' : 'filled'}
    />
  );

  const Zone: React.FC<{ id: string; titulo: string; keys: string[] }> = ({ id, titulo, keys }) => (
    <Paper
      variant="outlined"
      onDrop={(e) => onDrop(e, id)}
      onDragOver={allowDrop}
      sx={{
        p: 1.5,
        minHeight: 72,
        flex: 1,
        borderStyle: 'dashed',
        borderColor: keys.length ? 'primary.main' : 'divider',
        bgcolor: keys.length ? 'action.hover' : 'transparent',
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {titulo}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
        {keys.length ? keys.map((k) => renderChip(k, id)) : (
          <Typography variant="body2" color="text.disabled">
            arraste aqui
          </Typography>
        )}
      </Box>
    </Paper>
  );

  if (!municipioAtivo?.id) {
    return <Alert severity="info" sx={{ m: 2 }}>Selecione um municipio para usar o cubo.</Alert>;
  }

  return (
    <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Cubo interativo — drag &amp; drop
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Arraste as dimensoes para <b>Linhas</b> ou <b>Colunas</b> e escolha a metrica. Os dados
        respeitam o municipio ativo (<b>{municipioAtivo.nome}</b>).
      </Typography>

      {/* Seletor de fonte de dados — troca a paleta de dimensoes */}
      <FormControl size="small" sx={{ minWidth: 280, mb: 2 }}>
        <InputLabel id="fonte-label">Fonte de dados</InputLabel>
        <Select
          labelId="fonte-label"
          label="Fonte de dados"
          value={fonte}
          onChange={(e) => {
            const f = String(e.target.value);
            if (f !== fonte) loadFonte(f);
          }}
        >
          {fontes.map((f) => (
            <MenuItem key={f.key} value={f.key}>
              {f.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Paleta de dimensoes disponiveis */}
      <Paper variant="outlined" onDrop={(e) => onDrop(e, PALETTE)} onDragOver={allowDrop} sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Dimensoes disponiveis
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {paletteKeys.map((k) => renderChip(k, PALETTE))}
        </Box>
      </Paper>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="stretch">
        <Zone id={ROWS} titulo="Linhas" keys={rowKeys} />
        <Zone id={COLS} titulo="Colunas" keys={colKeys} />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="metric-label">Valor (metrica)</InputLabel>
          <Select
            labelId="metric-label"
            label="Valor (metrica)"
            value={metric}
            onChange={(e) => setMetric(String(e.target.value))}
          >
            {metricas.map((m) => (
              <MenuItem key={m.key} value={m.key}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !pivot ? (
        <Alert severity="info">Arraste ao menos uma dimensao para Linhas ou Colunas.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {pivot.rowDims.map((d) => (
                  <TableCell key={d.key} sx={{ fontWeight: 700 }}>
                    {d.label}
                  </TableCell>
                ))}
                {pivot.hasCols ? (
                  pivot.colTuples.map((ct) => (
                    <TableCell key={ct} align="right" sx={{ fontWeight: 700 }}>
                      {ct || '(vazio)'}
                    </TableCell>
                  ))
                ) : (
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {pivot.valLabel}
                  </TableCell>
                )}
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Total
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pivot.rowTuples.map((rt) => (
                <TableRow key={rt} hover>
                  {pivot.rowDims.length ? (
                    pivot.rowVals[rt].map((v, i) => <TableCell key={i}>{v || '(vazio)'}</TableCell>)
                  ) : (
                    <TableCell>Total geral</TableCell>
                  )}
                  {pivot.hasCols ? (
                    pivot.colTuples.map((ct) => (
                      <TableCell key={ct} align="right">
                        {pivot.cell[`${rt}||${ct}`] != null ? nf.format(pivot.cell[`${rt}||${ct}`]) : ''}
                      </TableCell>
                    ))
                  ) : (
                    <TableCell align="right">{nf.format(pivot.rowTotal[rt] || 0)}</TableCell>
                  )}
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {nf.format(pivot.rowTotal[rt] || 0)}
                  </TableCell>
                </TableRow>
              ))}
              {/* Linha de totais */}
              <TableRow>
                <TableCell colSpan={Math.max(pivot.rowDims.length, 1)} sx={{ fontWeight: 700 }}>
                  Total
                </TableCell>
                {pivot.hasCols ? (
                  pivot.colTuples.map((ct) => (
                    <TableCell key={ct} align="right" sx={{ fontWeight: 700 }}>
                      {nf.format(pivot.colTotal[ct] || 0)}
                    </TableCell>
                  ))
                ) : (
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {nf.format(pivot.grand)}
                  </TableCell>
                )}
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {nf.format(pivot.grand)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default CuboInterativo;
